/* Native video library. No autoplay, tracking, private-content fetch or shared theme mutation. */
if (!customElements.get('tb-exercise-group')) {
  customElements.define('tb-exercise-group', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.panels = [...this.querySelectorAll('[data-exercise-panel]')];
      this.choices = [...this.querySelectorAll('[data-exercise-choice]')];
      this.previous = this.querySelector('[data-exercise-prev]');
      this.next = this.querySelector('[data-exercise-next]');
      if (!this.panels.length || this.panels.length !== this.choices.length) return;
      this.classList.add('is-enhanced');
      this.querySelector('[data-exercise-controls]').hidden = false;
      for (const panel of this.panels) {
        const video = panel.querySelector('video');
        const play = panel.querySelector('[data-exercise-play]');
        const error = panel.querySelector('[data-exercise-error]');
        if (!video || !play) continue;
        video.controls = false;
        play.hidden = false;
        const failedSources = new Set();
        const sources = [...video.querySelectorAll('source')];
        // When every <source> fails, browsers can leave play() pending without
        // dispatching a MediaError on <video>. Surface that native failure too.
        for (const source of sources) {
          source.addEventListener('error', () => {
            failedSources.add(source);
            if (failedSources.size !== sources.length || panel.hidden) return;
            video.pause();
            play.hidden = false;
            video.controls = true;
            error.hidden = false;
          }, { signal });
        }
        play.addEventListener('click', async () => {
          failedSources.clear();
          error.hidden = true;
          play.hidden = true;
          video.controls = true;
          try {
            if (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) video.load();
            await video.play();
            // A late play promise must not revive a panel selected out of view.
            if (panel.hidden || !this.isConnected) video.pause();
          } catch (cause) {
            if (panel.hidden || !this.isConnected) return;
            play.hidden = false;
            video.controls = true;
            error.hidden = false;
          }
        }, { signal });
        video.addEventListener('error', () => {
          if (!panel.hidden) {
            error.hidden = false;
            play.hidden = false;
            video.controls = true;
          }
        }, { signal });
        video.addEventListener('play', () => {
          // Only one native demonstration plays in the document at a time.
          for (const other of document.querySelectorAll('tb-exercise-group video')) {
            if (other !== video && !other.paused) other.pause();
          }
          play.hidden = true;
          error.hidden = true;
        }, { signal });
      }
      this.choices.forEach((choice, index) => {
        choice.addEventListener('click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.select(index, true);
        }, { signal });
        choice.addEventListener('keydown', event => {
          let nextIndex;
          if (event.key === 'ArrowDown') nextIndex = Math.min(index + 1, this.choices.length - 1);
          if (event.key === 'ArrowUp') nextIndex = Math.max(index - 1, 0);
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.choices.length - 1;
          if (nextIndex === undefined) return;
          event.preventDefault();
          this.select(nextIndex, true);
          this.choices[nextIndex].focus({ preventScroll: true });
          this.revealChoice(nextIndex);
        }, { signal });
      });
      this.previous?.addEventListener('click', () => { this.select(this.selected - 1, true); this.revealChoice(this.selected); }, { signal });
      this.next?.addEventListener('click', () => { this.select(this.selected + 1, true); this.revealChoice(this.selected); }, { signal });
      const fromHash = () => {
        let target;
        try { target = decodeURIComponent(location.hash.slice(1)); } catch { return -1; }
        return this.panels.findIndex(panel => panel.id === target);
      };
      const index = fromHash();
      this.select(index < 0 ? 0 : index, false);
      window.addEventListener('hashchange', () => {
        const nextIndex = fromHash();
        if (nextIndex >= 0) this.select(nextIndex, false);
      }, { signal });
      // Selecting a video block in the Shopify editor reveals its actual demonstration.
      document.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel === event.target || panel.contains(event.target));
        if (index >= 0) this.select(index, false);
      }, { signal });
    }
    select(index, updateHash) {
      if (index < 0 || index >= this.panels.length) return;
      this.selected = index;
      this.panels.forEach((panel, i) => {
        const active = i === index;
        if (!active) panel.querySelector('video')?.pause();
        panel.hidden = !active;
        this.choices[i].setAttribute('aria-current', String(active));
      });
      if (this.previous) this.previous.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.panels.length - 1;
      if (updateHash) history.replaceState(history.state, '', '#' + encodeURIComponent(this.panels[index].id));
    }
    revealChoice(index) {
      const list = this.querySelector('.tb-tuto-playlist');
      const choice = this.choices[index];
      if (!list || !choice) return;
      const viewport = list.getBoundingClientRect();
      const item = choice.getBoundingClientRect();
      if (item.top < viewport.top) list.scrollTop -= viewport.top - item.top;
      else if (item.bottom > viewport.bottom) list.scrollTop += item.bottom - viewport.bottom;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}
